import {
  CreateEventFormAnswersForm,
  createEventFormAnswersSchema,
  defaultCreateEventFormAnswersValues,
} from "@ac-expo/api";
import { EventFormQuestionOnEvent } from "@ac-expo/api-types";
import {
  Button,
  EventForm,
  Form,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@ac-expo/ui-components";
import { INTEREST_QUESTION_ID } from "@ac-expo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { EventType } from "@prisma/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useEventFormsContext } from "../../context/event-forms.context";
import { ArrowLeftIcon } from "../../icons";
import { EventFormBusinessSectorSelect } from "../BusinessSector/EventFormBusinessSectorSelect";

interface TicketFormData {
  ticketId: string;
  eventId: string;
  eventName: string;
  ticketName: string;
  ticketIndex: number;
  organiserId: string;
  eventType: EventType;
  questions: EventFormQuestionOnEvent[];
}

type ConsentValue = "listed-and-contacted" | "listed-only" | "not-listed";

const CONSENT_OPTIONS: { value: ConsentValue; label: string }[] = [
  {
    value: "listed-and-contacted",
    label:
      "Apparaître dans la liste des inscrits et pouvoir être contacté par des exposants de mon secteur d'activité",
  },
  {
    value: "listed-only",
    label: "Apparaître dans la liste des inscrits",
  },
  {
    value: "not-listed",
    label: "Ne pas apparaître dans la liste des inscrits",
  },
];

function toConsentValue(
  wantsToAppearInRegisteredList: boolean,
  wantsExhibitorContact: boolean,
): ConsentValue {
  if (wantsToAppearInRegisteredList && wantsExhibitorContact) return "listed-and-contacted";
  if (wantsToAppearInRegisteredList) return "listed-only";
  return "not-listed";
}

interface EventFormsStepProps {
  ticketForms: TicketFormData[];
  onBack: () => void;
  onComplete: () => void;
}

export function EventFormsStep({ ticketForms, onBack, onComplete }: EventFormsStepProps) {
  const [currentFormIndex, setCurrentFormIndex] = useState(0);

  const currentTicketForm = ticketForms[currentFormIndex];
  const totalForms = ticketForms.length;
  const isLastForm = currentFormIndex === totalForms - 1;
  const { saveFormAnswers, getFormAnswers } = useEventFormsContext();

  const form = useForm<CreateEventFormAnswersForm>({
    resolver: zodResolver(createEventFormAnswersSchema),
    defaultValues: {
      ...defaultCreateEventFormAnswersValues,
      eventId: currentTicketForm?.eventId || "",
    },
  });

  const { control, handleSubmit, reset, setValue, watch } = form;

  const wantsToAppearInRegisteredList = watch("wantsToAppearInRegisteredList");
  const wantsExhibitorContact = watch("wantsExhibitorContact");

  const consentValue = toConsentValue(
    wantsToAppearInRegisteredList ?? false,
    wantsExhibitorContact ?? false,
  );

  const handleConsentChange = useCallback(
    (value: string) => {
      setValue("wantsToAppearInRegisteredList", value !== "not-listed");
      setValue("wantsExhibitorContact", value === "listed-and-contacted");
    },
    [setValue],
  );

  const { interestQuestionData, otherQuestions, otherQuestionsFieldIndices } = useMemo(() => {
    const questions = currentTicketForm?.questions || [];
    const interestIndex = questions.findIndex((q) => q.question.id === INTEREST_QUESTION_ID);
    const othersWithIndices = questions
      .map((q, i) => ({ question: q, originalIndex: i }))
      .filter((item) => item.question.question.id !== INTEREST_QUESTION_ID);
    return {
      interestQuestionData:
        interestIndex >= 0 ? { question: questions[interestIndex], index: interestIndex } : null,
      otherQuestions: othersWithIndices.map((item) => item.question),
      otherQuestionsFieldIndices: othersWithIndices.map((item) => item.originalIndex),
    };
  }, [currentTicketForm?.questions]);

  useEffect(() => {
    if (!currentTicketForm) return;

    const ticketId = currentTicketForm.ticketId;
    const storedFormData = getFormAnswers(ticketId);

    const answersToUse = storedFormData
      ? storedFormData.answers
      : currentTicketForm.questions.map((questionOnEvent) => ({
          questionId: questionOnEvent.question.id,
          answer: "",
          answerList: [],
        }));

    const isProfessionalEvent = currentTicketForm.eventType === EventType.professionalFair;

    reset({
      eventId: currentTicketForm.eventId,
      answers: answersToUse,
      wantsToAppearInRegisteredList:
        storedFormData?.wantsToAppearInRegisteredList ?? isProfessionalEvent,
      wantsExhibitorContact: storedFormData?.wantsExhibitorContact ?? isProfessionalEvent,
    });
  }, [currentTicketForm, reset, getFormAnswers]);

  const onSubmit = (data: CreateEventFormAnswersForm) => {
    if (!currentTicketForm) return;

    saveFormAnswers(currentTicketForm.ticketId, data);

    if (isLastForm) {
      onComplete();
    } else {
      setCurrentFormIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentFormIndex > 0) {
      setCurrentFormIndex((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  if (!currentTicketForm) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-2">
          <button onClick={handleBack}>
            <ArrowLeftIcon width={20} height={20} />
          </button>
          <div>
            <p className="text-sm text-gray-600">
              {totalForms > 1 ? `Billet ${currentFormIndex + 1} sur ${totalForms}` : "Formulaire"}
            </p>
            <h3 className="font-semibold">{currentTicketForm.eventName}</h3>
            <p className="text-xs text-gray-500">{currentTicketForm.ticketName}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {interestQuestionData && (
              <EventFormBusinessSectorSelect
                question={interestQuestionData.question.question}
                control={control}
                index={interestQuestionData.index}
                eventId={currentTicketForm.eventId}
              />
            )}
            {otherQuestions.length > 0 && (
              <EventForm
                eventFormQuestionsOnEvent={otherQuestions}
                control={control}
                isReadOnly={false}
                fieldIndices={otherQuestionsFieldIndices}
              />
            )}
            {currentTicketForm.eventType === EventType.professionalFair && (
              <RadioGroup
                value={consentValue}
                onValueChange={handleConsentChange}
                className="flex flex-col gap-3"
              >
                <p className="text-sm font-medium">Gestion de mes données personnelles</p>
                {CONSENT_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-start gap-2">
                    <RadioGroupItem
                      value={option.value}
                      id={`consent-${option.value}`}
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor={`consent-${option.value}`}
                      className="text-sm font-normal leading-snug"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
          </form>
        </Form>
      </div>

      <div className="flex-[0.1] border-t p-4">
        <div className="flex justify-end">
          <Button type="submit" variant="blue" onClick={handleSubmit(onSubmit)}>
            {isLastForm ? "Terminer" : "Suivant"}
          </Button>
        </div>
      </div>
    </div>
  );
}
